import type { QueryResult } from '@apollo/client/react';
import Button from '@aurora/shared-client/components/common/Button/Button';
import { ButtonVariant } from '@aurora/shared-client/components/common/Button/enums';
import {
  ToastAlertVariant,
  ToastVariant
} from '@aurora/shared-client/components/common/ToastAlert/enums';
import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import type { FeaturedWidgetInstance } from '@aurora/shared-client/components/context/FeaturedWidgetContext/FeaturedWidgetContext';
import useToasts from '@aurora/shared-client/components/context/ToastContext/useToasts';
import useMutationWithTracing from '@aurora/shared-client/components/useMutationWithTracing';
import type {
  FeaturedWidgetNotFoundError,
  IdeaBoardNotFoundError,
  IdeaStatusDetails,
  InvalidNodeTypeError
} from '@aurora/shared-generated/types/graphql-schema-types';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import { getLog } from '@aurora/shared-utils/log';
import type { GraphQLError } from 'graphql';
import React, { useContext, useState } from 'react';
import { Modal, useClassNameMapper } from 'react-bootstrap';
import type {
  FeaturedIdeaStatusesWidgetQuery,
  FeaturedIdeaStatusesWidgetQueryVariables,
  StoreFeaturedIdeaStatusesMutation,
  StoreFeaturedIdeaStatusesMutationVariables
} from '../../../../types/graphql-types';
import EditContext from '../../../context/EditContext/EditContext';
import useTranslation from '../../../useTranslation';
import EditFeaturedList from '../../EditFeaturedList/EditFeaturedList';
import ideaStatusesQuery from '../FeaturedIdeaStatusesWidget.query.graphql';
import storeIdeaStatusesMutation from '../StoreFeaturedIdeaStatuses.mutation.graphql';
import useFeaturedIdeaStatusesWidget from '../FeaturedIdeaStatusesWidget/useFeaturedIdeaStatusesWidget';
import localStyles from './AddFeaturedIdeaStatusesModal.module.pcss';
import AddFeaturedIdeaStatusesDropdown from './AddFeaturedIdeaStatusesDropdown';
import FeaturedWidgetLastModifiedInfo from '../../FeaturedWidgetLastModifiedInfo/FeaturedWidgetLastModifiedInfo';

interface Props {
  /**
   * Whether to show the modal
   */
  show: boolean;
  /**
   * Callback invoked to hide the modal
   */
  onHide: () => void;
  /**
   * Variables for the FeaturedIdeaStatusesWidget query
   */
  ideaStatusesQueryVariables: FeaturedIdeaStatusesWidgetQueryVariables;
}

const log = getLog(module);

/**
 * Renders a modal with an input used to search for ideaStatuses to add to the FeaturedIdeaStatusesWidget
 *
 * @author rishabh.jivan
 */
const AddFeaturedIdeaStatusesModal: React.FC<React.PropsWithChildren<Props>> = ({
  show,
  onHide,
  ideaStatusesQueryVariables
}) => {
  const {
    contextNode: { nodeType, title: nodeTitle, id: contextNodeId }
  } = useContext(AppContext);
  const { onChange, quilt } = useContext(EditContext);
  const cx = useClassNameMapper(localStyles);
  const i18n = useTranslation(EndUserComponent.ADD_FEATURED_IDEA_STATUSES_MODAL);
  const { formatMessage, loading: textLoading, FormattedMessage } = i18n;
  const { addToast } = useToasts();
  const [selectedStatuses, setSelectedStatuses] = useState<Array<IdeaStatusDetails>>([]);

  /**
   * Updates the selected ideaStatuses in component state with the original ideaStatuses stored on the widget
   *
   * @param data the data from the FeaturedIdeaStatusesWidgetQuery result
   */
  function initializeSelectedStatuses(data: FeaturedIdeaStatusesWidgetQuery): void {
    if (data.featuredStatusesWidget.statuses.edges) {
      setSelectedStatuses(
        data.featuredStatusesWidget.statuses.edges.map(({ node }) => node as IdeaStatusDetails)
      );
    }
  }

  const {
    data: ideaStatusesQueryData,
    loading: ideaStatusesQueryLoading,
    error: ideaStatusesQueryError
  }: QueryResult<
    FeaturedIdeaStatusesWidgetQuery,
    FeaturedIdeaStatusesWidgetQueryVariables
  > = useFeaturedIdeaStatusesWidget(
    {
      ...ideaStatusesQueryVariables
    },
    !show,
    'no-cache',
    (data: FeaturedIdeaStatusesWidgetQuery) => initializeSelectedStatuses(data)
  );

  const [updateFeaturedIdeaStatuses, { loading: updateFeaturedIdeaStatusesMutationLoading }] =
    useMutationWithTracing<
      StoreFeaturedIdeaStatusesMutation,
      StoreFeaturedIdeaStatusesMutationVariables
    >(module, storeIdeaStatusesMutation);

  /**
   * Resets the selected ideaStatuses and closes the modal
   */
  function onModalClose(): void {
    initializeSelectedStatuses(ideaStatusesQueryData);
    onHide();
  }

  /**
   * Updates the ideaStatuses used by the featured ideaStatus component
   */
  async function onUpdateFeaturedIdeaStatuses(): Promise<void> {
    const ideaStatusIds: Array<string> = selectedStatuses.map(
      (ideaStatus: IdeaStatusDetails) => ideaStatus.statusKey
    );
    const { instanceId, quiltId, ideaBoardId } = ideaStatusesQueryVariables;
    // immediately persist idea statuses if changes are submitted outside of page builder
    if (!quilt) {
      const { data, errors } = await updateFeaturedIdeaStatuses({
        variables: {
          instanceId,
          statusKeys: ideaStatusIds,
          quiltId,
          ideaBoardId
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: ideaStatusesQuery,
            variables: ideaStatusesQueryVariables
          }
        ]
      });
      if (errors || data?.storeFeaturedIdeaStatuses?.errors) {
        errors?.map((error: GraphQLError) =>
          log.error('error storing featured ideaStatuses', error)
        );
        data?.storeFeaturedIdeaStatuses?.errors?.map(
          (error: FeaturedWidgetNotFoundError | IdeaBoardNotFoundError | InvalidNodeTypeError) =>
            log.error('error storing featured ideaStatuses', error)
        );
        addToast({
          toastVariant: ToastVariant.BANNER,
          alertVariant: ToastAlertVariant.DANGER,
          title: formatMessage('failureTitle'),
          message: formatMessage('failureMessage'),
          autohide: true,
          delay: 4000,
          id: `AddFeaturedIdeaStatusesModal-StoreFeaturedIdeaStatuses-${instanceId}-Error`
        });
        return;
      } else {
        addToast({
          toastVariant: ToastVariant.FLYOUT,
          alertVariant: ToastAlertVariant.SUCCESS,
          title: formatMessage('successTitle'),
          message: formatMessage('successMessage'),
          autohide: true,
          delay: 4000,
          id: `AddFeaturedIdeaStatusesModal-StoreFeaturedIdeaStatuses-${instanceId}-Success`
        });
        onHide();
      }
    } else {
      // store the changes in the page editor session and invoke the mutation during publish
      const sessionInstance: FeaturedWidgetInstance = {
        componentId: EndUserComponent.FEATURED_IDEA_STATUSES_WIDGET,
        instanceId: ideaStatusesQueryVariables.instanceId,
        featuredItemIds: ideaStatusIds,
        coreNodeId: ideaStatusesQueryVariables.ideaBoardId
      };

      onChange(quilt, null, sessionInstance);
      onHide();
    }
  }

  if (ideaStatusesQueryError) {
    log.error(
      `error retrieving ideaStatuses for FeaturedIdeaStatusesWidget with instance id ${ideaStatusesQueryVariables.instanceId}`,
      ideaStatusesQueryError.message
    );
  }

  if (textLoading || ideaStatusesQueryLoading || ideaStatusesQueryError) {
    return null;
  }

  /**
   * Renders the timestamp and user login associated with the last modification to the widget contents
   */
  function renderLastModifiedHistory(): React.ReactElement {
    const { lastModified, lastModifiedUser } = ideaStatusesQueryData.featuredStatusesWidget;
    return (
      <FeaturedWidgetLastModifiedInfo
        lastModified={lastModified}
        lastModifiedUser={lastModifiedUser.login}
      />
    );
  }

  /**
   * Renders the description that appears the modal header
   */
  function renderDescription(): React.ReactElement {
    return (
      <span className={cx('lia-description')}>
        <FormattedMessage
          // use a different description text if the modal is rendered inside of page builder
          id={quilt ? 'PageEditor.header.description' : 'header.description'}
          values={{ bold: (chunks: string) => <strong>{chunks}</strong>, nodeType, nodeTitle }}
        />
      </span>
    );
  }

  return (
    <Modal
      show={show}
      onHide={() => onModalClose()}
      size="sm"
      data-testid="AddFeaturedIdeaStatusesModal"
    >
      <Modal.Header closeButton>
        {/*use a different header text if the modal is rendered inside of page builder*/}
        <Modal.Title>{formatMessage(quilt ? 'PageEditor.header' : 'header')}</Modal.Title>
      </Modal.Header>
      {renderDescription()}
      <Modal.Body>
        {selectedStatuses.length > 0 && (
          <EditFeaturedList
            items={selectedStatuses}
            updateItems={ideaStatuses => setSelectedStatuses(ideaStatuses)}
            isSubmitting={updateFeaturedIdeaStatusesMutationLoading}
            renderItemComponent={(ideaStatus: IdeaStatusDetails) => <>{ideaStatus.text}</>}
          />
        )}
        <AddFeaturedIdeaStatusesDropdown
          contextNodeId={contextNodeId}
          excludeStatusKeys={selectedStatuses.map(ideaStatus => ideaStatus.statusKey)}
          onChange={(item: IdeaStatusDetails) => {
            setSelectedStatuses(previousIdeaStatuses => [item, ...previousIdeaStatuses]);
          }}
        />
        {!!ideaStatusesQueryData?.featuredStatusesWidget?.lastModifiedUser && (
          <div className={cx('lia-g-mt-20')}>{renderLastModifiedHistory()}</div>
        )}
        <div className={cx('lia-g-modal-buttons-wrap lia-g-mt-20')}>
          <Button
            size="lg"
            loading={updateFeaturedIdeaStatusesMutationLoading}
            onClick={async () => await onUpdateFeaturedIdeaStatuses()}
            data-testid="AddFeaturedIdeaStatusesModal.Submit.Btn"
          >
            {/*use preview text as submit button text if the modal is rendered inside of page builder*/}
            {formatMessage(quilt ? 'previewBtn' : 'submitBtn')}
          </Button>
          <Button
            variant={ButtonVariant.LIGHT}
            size="lg"
            onClick={() => onModalClose()}
            disabled={updateFeaturedIdeaStatusesMutationLoading}
            data-testid="AddFeaturedIdeaStatusesModal.Cancel.Btn"
          >
            {formatMessage('cancelBtn')}
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};
export default AddFeaturedIdeaStatusesModal;
